/**
 * Rich interactive calculators & visuals for bespoke SEO pages.
 */

import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Zap, TrendingDown } from 'lucide-react';
import { EV_CONSTANTS, SERVICE_AGREEMENT } from '@/components/seo/content/ContentRegistry';

const serif = { fontFamily: '"Playfair Display", Georgia, serif' };

// ── EV charging cost + savings calculator ───────────────────────────────
// Lets the visitor drag km/year and (optionally) consumption to see real numbers.
export function EVChargingCalculator({
  defaultKm = 15000,
  kwhPer100km = 22,
  spotPrice = 1.05, // kr/kWh incl. transport+taxes rough avg for home charging
  title = 'Beregn din opladning hjemme',
}) {
  const [km, setKm] = useState(defaultKm);
  const [cons, setCons] = useState(kwhPer100km);
  const homePct = 0.9;

  const r = useMemo(() => {
    const evKwh = Math.round((km * (cons / 100)) * homePct);
    const baseCost = Math.round(evKwh * spotPrice);
    const laderabat = Math.round(evKwh * 0.10); // 10 øre/kWh
    const smartCharge = Math.round(evKwh * spotPrice * 0.22); // ~22% on the energy part
    const afterEnkel = Math.max(0, baseCost - laderabat - smartCharge);
    return { evKwh, baseCost, laderabat, smartCharge, afterEnkel, totalSaving: laderabat + smartCharge };
  }, [km, cons, spotPrice]);

  return (
    <div className="bg-white rounded-2xl p-6 md:p-8 border border-[#263A43]/12 shadow-sm">
      <h3 className="text-xl font-bold text-[#1F2E33] mb-6 text-center" style={serif}>{title}</h3>

      <div className="grid sm:grid-cols-2 gap-6 mb-6">
        <div>
          <label className="text-sm font-semibold text-gray-700 flex justify-between mb-2">
            <span>Årlig kørsel</span><span className="text-[#2F424D] font-bold">{km.toLocaleString('da-DK')} km</span>
          </label>
          <input type="range" min="5000" max="40000" step="1000" value={km}
            onChange={(e) => setKm(parseInt(e.target.value))} className="w-full accent-[#D4A62A]" />
        </div>
        <div>
          <label className="text-sm font-semibold text-gray-700 flex justify-between mb-2">
            <span>Forbrug</span><span className="text-[#2F424D] font-bold">{cons} kWh/100 km</span>
          </label>
          <input type="range" min="13" max="28" step="0.5" value={cons}
            onChange={(e) => setCons(parseFloat(e.target.value))} className="w-full accent-[#D4A62A]" />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label="Hjemmeopladning" value={`${r.evKwh.toLocaleString('da-DK')} kWh`} sub="per år (90% hjemme)" />
        <Stat label="Laderabat" value={`${r.laderabat.toLocaleString('da-DK')} kr.`} sub="10 øre/kWh" accent />
        <Stat label="Saving Mode" value={`~${r.smartCharge.toLocaleString('da-DK')} kr.`} sub="~22% på energien" accent />
        <Stat label="Samlet besparelse" value={`~${r.totalSaving.toLocaleString('da-DK')} kr.`} sub="per år" gold />
      </div>

      <div className="bg-[#1F2E33] rounded-xl p-5 text-white flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <p className="text-sm text-white/70">Estimeret opladning hos enkel efter rabat</p>
          <p className="text-2xl font-bold text-[#E9C766]">~{r.afterEnkel.toLocaleString('da-DK')} kr./år</p>
        </div>
        <Link to="/elaftale">
          <Button className="bg-[#D4A62A] hover:bg-[#c29625] text-[#1F2E33] font-bold">
            Se el-aftaler <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
      <p className="text-[11px] text-gray-400 mt-3 text-center">
        Vejledende estimat. Spotprisen varierer time for time. Saving Mode-besparelse er ikke garanteret (historisk 15–30%).
      </p>
    </div>
  );
}

function Stat({ label, value, sub, accent, gold }) {
  return (
    <div className={`rounded-xl p-4 border ${gold ? 'bg-[#D4A62A] border-[#D4A62A]' : accent ? 'bg-[#e8f5e9] border-[#0f5c4a]/15' : 'bg-[#F6F3EC] border-[#263A43]/10'}`}>
      <p className={`text-xs ${gold ? 'text-[#1F2E33]/70' : 'text-gray-500'}`}>{label}</p>
      <p className={`text-lg font-bold ${gold ? 'text-[#1F2E33]' : 'text-[#1F2E33]'}`}>{value}</p>
      {sub && <p className={`text-[10px] ${gold ? 'text-[#1F2E33]/60' : 'text-gray-400'}`}>{sub}</p>}
    </div>
  );
}

// ── Saving Mode timeline visual (24h price bars, cheap hours highlighted) ──
export function SmartChargeTimeline() {
  // Representative 24h spot shape (relative). Night = cheap, evening peak = expensive.
  const hours = [
    0.45, 0.38, 0.34, 0.32, 0.35, 0.42, 0.62, 0.85, 0.95, 0.80, 0.70, 0.66,
    0.64, 0.62, 0.65, 0.72, 0.88, 1.00, 0.98, 0.86, 0.74, 0.66, 0.56, 0.50,
  ];
  const min = Math.min(...hours);
  const cheapThreshold = min * 1.25;
  return (
    <div className="bg-white rounded-2xl p-6 border border-[#263A43]/12 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-lg bg-[#D4A62A] flex items-center justify-center"><TrendingDown className="w-5 h-5 text-[#1F2E33]" /></div>
        <div>
          <h3 className="font-bold text-[#1F2E33]">Sådan vælger Saving Mode de billige timer</h3>
          <p className="text-xs text-gray-500">Et typisk døgn – spotpris time for time</p>
        </div>
      </div>
      <div className="flex items-end gap-[3px] h-36">
        {hours.map((h, i) => {
          const cheap = h <= cheapThreshold;
          return (
            <div key={i} className="flex-1 h-full flex flex-col items-center justify-end">
              <div
                className={`w-full rounded-t ${cheap ? 'bg-[#0f5c4a]' : 'bg-[#cdd6da]'}`}
                style={{ height: `${Math.round(h * 100)}%`, minHeight: '4px' }}
                title={`Kl. ${i}:00`}
              />
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-[10px] text-gray-400 mt-1">
        <span>00</span><span>06</span><span>12</span><span>18</span><span>23</span>
      </div>
      <div className="flex items-center gap-4 mt-4 text-xs">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#0f5c4a]" /> Saving Mode lader her (billigst)</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#cdd6da]" /> Dyre timer – undgås</span>
      </div>
    </div>
  );
}

// ── Electricity bill breakdown bar (what makes up the kWh price) ─────────
export function PriceBreakdownBar() {
  const parts = [
    { label: 'Spotpris (Nord Pool)', pct: 38, color: '#2F424D' },
    { label: 'Transport / nettarif', pct: 24, color: '#5E7B8C' },
    { label: 'Elafgift', pct: 18, color: '#D4A62A' },
    { label: 'Moms (25%)', pct: 20, color: '#b9a36a' },
  ];
  return (
    <div className="bg-white rounded-2xl p-6 border border-[#263A43]/12 shadow-sm">
      <h3 className="font-bold text-[#1F2E33] mb-4">Hvad består din kWh-pris af?</h3>
      <div className="flex h-10 rounded-lg overflow-hidden mb-4">
        {parts.map((p, i) => (
          <div key={i} style={{ width: `${p.pct}%`, background: p.color }} className="flex items-center justify-center text-white text-xs font-semibold" title={`${p.label}: ~${p.pct}%`}>
            {p.pct}%
          </div>
        ))}
      </div>
      <ul className="grid grid-cols-2 gap-2 text-sm">
        {parts.map((p, i) => (
          <li key={i} className="flex items-center gap-2">
            <span className="w-3 h-3 rounded" style={{ background: p.color }} />
            <span className="text-gray-700">{p.label}</span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-gray-500 mt-3">
        Fordelingen er vejledende og svinger time for time. Hos enkel energi lægger vi <strong>0 øre i tillæg</strong> til spotprisen – du betaler kun den rå markedspris plus et fast abonnement fra 18 kr./md.
      </p>
    </div>
  );
}